import {
  formatAudCompact,
  isRefundEntry,
  netVerifiedPaidFromTermPayments,
  pendingRefundTotalFromTermPayments,
} from "./termPaymentUtils";

// A credit adjustment is stored as two linked rows: a refund on the overpaid term and a payment on
// the term that takes the credit. They share an adjustmentUuid and are verified or rejected together.

const cents = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const isCreditAdjustment = (payment) =>
  Boolean(String(payment?.adjustmentUuid ?? "").trim());

export const creditAdjustmentLabel = (payment) => {
  const other =
    payment?.adjustmentTermNumber != null ? `T${payment.adjustmentTermNumber}` : "another term";
  return isRefundEntry(payment?.entryType)
    ? `Credit moved to ${other}`
    : `Credit moved from ${other}`;
};

/** Mirrors TermPaymentLedgerService#creditAvailable; the server re-checks it on every move. */
export const termCreditAvailable = (payments, agreed) =>
  cents(
    Math.max(
      0,
      netVerifiedPaidFromTermPayments(payments) -
        (Number(agreed) || 0) -
        pendingRefundTotalFromTermPayments(payments)
    )
  );

export const defaultCreditMoveAmount = (creditAvailable, targetDue) =>
  cents(Math.min(Number(creditAvailable) || 0, Math.max(0, Number(targetDue) || 0)));

export const overpaidAfterCreditMove = (amount, targetDue) =>
  cents(Math.max(0, (Number(amount) || 0) - Math.max(0, Number(targetDue) || 0)));

export const validateCreditMove = ({ creditAvailable, target, amount, reason }) => {
  if (!target) return "Pick the term that should take the credit.";
  const value = cents(amount);
  if (!(value > 0)) return "Enter an amount above zero.";
  if (value > cents(creditAvailable)) {
    return `You can move at most ${formatAudCompact(creditAvailable)} from this term.`;
  }
  if (!String(reason ?? "").trim()) return "A reason is required for a credit adjustment.";
  return null;
};

/**
 * The pending-verification list has one entry per payment row, so a credit move shows up twice.
 * Verifying or rejecting either half acts on both, so the queue shows them as one item, led by the
 * outgoing half.
 */
export const groupCreditAdjustmentPairs = (items) => {
  const list = items || [];
  const halvesByUuid = new Map();
  list.forEach((item) => {
    const uuid = item?.payment?.adjustmentUuid;
    if (uuid) halvesByUuid.set(uuid, [...(halvesByUuid.get(uuid) || []), item]);
  });

  const emitted = new Set();
  return list.flatMap((item) => {
    const uuid = item?.payment?.adjustmentUuid;
    if (!uuid) return [item];
    if (emitted.has(uuid)) return [];
    emitted.add(uuid);
    const halves = halvesByUuid.get(uuid);
    const out = halves.find((h) => isRefundEntry(h.payment.entryType)) ?? null;
    const into = halves.find((h) => !isRefundEntry(h.payment.entryType)) ?? null;
    return [{ ...(out ?? into), creditAdjustment: { out, in: into } }];
  });
};

/** The term a grouped credit move leaves and the term it lands on. */
export const creditMoveTermNumbers = (item) => {
  const out = item?.creditAdjustment?.out;
  const into = item?.creditAdjustment?.in;
  return {
    from: out?.term?.termNumber ?? into?.payment?.adjustmentTermNumber ?? null,
    to: into?.term?.termNumber ?? out?.payment?.adjustmentTermNumber ?? null,
  };
};
